import api from './api';

export const ticketService = {
  getAll: (params = {}) => {
    // Strip empty-string values so the backend validator doesn't reject them
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined));
    return api.get('/api/tickets', { params: cleaned });
  },
  getById: (id) => api.get(`/api/tickets/${id}`),
  create: (data) => {
    // Send as multipart/form-data when files are present
    if (data instanceof FormData) {
      return api.post('/api/tickets', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post('/api/tickets', data);
  },
  update: (id, data) => api.put(`/api/tickets/${id}`, data),
  delete: (id) => api.delete(`/api/tickets/${id}`),
  addComment: (id, data) => api.post(`/api/tickets/${id}/comments`, data),
  rate: (id, data) => api.post(`/api/tickets/${id}/rate`, data),
  // Bulk & merge
  bulkUpdate: (data) => api.patch('/api/tickets/bulk', data),
  merge: (sourceId, targetId) => api.post(`/api/tickets/${sourceId}/merge/${targetId}`),
  // Escalation
  escalate: (id, data = {}) => api.post(`/api/tickets/${id}/escalate`, data),
  // Time tracking
  logTime: (id, data) => api.post(`/api/tickets/${id}/time`, data),
  // Linking
  link: (id, data) => api.post(`/api/tickets/${id}/link`, data),
  unlink: (id, targetId) => api.delete(`/api/tickets/${id}/link/${targetId}`),
  // Watch
  watch: (id) => api.post(`/api/tickets/${id}/watch`),
  unwatch: (id) => api.delete(`/api/tickets/${id}/watch`),
  // KB suggestions
  getSuggestions: (id) => api.get(`/api/tickets/${id}/suggestions`),
  // Clone
  clone: (id) => api.post(`/api/tickets/${id}/clone`),
  // Restore
  restore: (id) => api.post(`/api/tickets/${id}/restore`),
  // Approve / reject (admin)
  approve: (id, data) => api.post(`/api/tickets/${id}/approve`, data),
  // Export
  exportCSV: (params = {}) => api.get('/api/tickets/export', { params, responseType: 'blob' }),
};

export const articleService = {
  getAll: (params = {}) => api.get('/api/articles', { params }),
  getBySlug: (slug) => api.get(`/api/articles/${slug}`),
  getById: (id) => api.get(`/api/articles/${id}`),
  create: (data) => api.post('/api/articles', data),
  update: (id, data) => api.put(`/api/articles/${id}`, data),
  delete: (id) => api.delete(`/api/articles/${id}`),
  voteHelpful: (id, helpful) => api.post(`/api/articles/${id}/helpful`, { helpful }),
};

export const userService = {
  getAll: (params = {}) => api.get('/api/users', { params }),
  getById: (id) => api.get(`/api/users/${id}`),
  getAgents: () => api.get('/api/users/agents'),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  updateRole: (id, role) => api.put(`/api/users/${id}`, { role }),
  delete: (id) => api.delete(`/api/users/${id}`),
  remove: (id) => api.delete(`/api/users/${id}`),
  invite: (data) => api.post('/api/users/invite', data),
  unlock: (id) => api.post(`/api/users/${id}/unlock`),
  resendInvite: (id) => api.post(`/api/users/${id}/resend-invite`),
};

export const emailTemplateService = {
  getAll: () => api.get('/api/email-templates'),
  getById: (id) => api.get(`/api/email-templates/${id}`),
  create: (data) => api.post('/api/email-templates', data),
  update: (id, data) => api.put(`/api/email-templates/${id}`, data),
  delete: (id) => api.delete(`/api/email-templates/${id}`),
};

export const tenantService = {
  getConfig: () => api.get('/api/tenant/config'),
  updateConfig: (data) => api.put('/api/tenant/config', data),
  getStats: (params = {}) => api.get('/api/tenant/stats', { params }),
};

export const chatService = {
  getSessions: (params = {}) => api.get('/api/chat', { params }),
  start: (data = {}) => api.post('/api/chat', data),
  getSession: (id) => api.get(`/api/chat/${id}`),
  sendMessage: (id, body, files = []) => {
    if (files.length > 0) {
      const fd = new FormData();
      if (body) fd.append('body', body);
      files.forEach((f) => fd.append('attachments', f));
      return api.post(`/api/chat/${id}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post(`/api/chat/${id}/messages`, { body });
  },
  join: (id) => api.post(`/api/chat/${id}/join`),
  close: (id) => api.post(`/api/chat/${id}/close`),
  convertToTicket: (id, data = {}) => api.post(`/api/chat/${id}/convert`, data),
};

export const notificationService = {
  getAll: (params = {}) => api.get('/api/notifications', { params }),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/api/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/api/notifications/read-all'),
  delete: (id) => api.delete(`/api/notifications/${id}`),
};

export const cannedResponseService = {
  getAll: (params = {}) => api.get('/api/canned-responses', { params }),
  getById: (id) => api.get(`/api/canned-responses/${id}`),
  create: (data) => api.post('/api/canned-responses', data),
  update: (id, data) => api.put(`/api/canned-responses/${id}`, data),
  delete: (id) => api.delete(`/api/canned-responses/${id}`),
  render: (id, context) => api.post(`/api/canned-responses/${id}/render`, { context }),
};

export const auditService = {
  getAll: (params = {}) => api.get('/api/audit-logs', { params }),
};

export const savedFilterService = {
  getAll: () => api.get('/api/saved-filters'),
  create: (data) => api.post('/api/saved-filters', data),
  delete: (id) => api.delete(`/api/saved-filters/${id}`),
};

export const webhookService = {
  getAll: (params = {}) => api.get('/api/webhooks', { params }),
  getById: (id) => api.get(`/api/webhooks/${id}`),
  create: (data) => api.post('/api/webhooks', data),
  update: (id, data) => api.put(`/api/webhooks/${id}`, data),
  delete: (id) => api.delete(`/api/webhooks/${id}`),
};

export const ticketTemplateService = {
  getAll: () => api.get('/api/ticket-templates'),
  getById: (id) => api.get(`/api/ticket-templates/${id}`),
  create: (data) => api.post('/api/ticket-templates', data),
  update: (id, data) => api.put(`/api/ticket-templates/${id}`, data),
  delete: (id) => api.delete(`/api/ticket-templates/${id}`),
};

export const agentGroupService = {
  getAll: () => api.get('/api/agent-groups'),
  getById: (id) => api.get(`/api/agent-groups/${id}`),
  create: (data) => api.post('/api/agent-groups', data),
  update: (id, data) => api.put(`/api/agent-groups/${id}`, data),
  delete: (id) => api.delete(`/api/agent-groups/${id}`),
};

export const draftService = {
  getAll: () => api.get('/api/drafts'),
  getForTicket: (ticketId) => api.get(`/api/drafts/ticket/${ticketId}`),
  save: (data) => api.post('/api/drafts', data),
  delete: (id) => api.delete(`/api/drafts/${id}`),
};
